import { useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDashed,
  Clock,
  Dumbbell,
  Utensils,
  XCircle,
} from "lucide-react";
import type {
  StudentDietExecution,
  StudentDietExecutionItem,
  StudentExecution,
  StudentExecutionItem,
} from "@/lib/types";
import { cn } from "@/lib/cn";

/**
 * Timeline unificada do histórico — portada de
 * `frontend/components/student-portal/HistoryTimeline.tsx` (legado Next).
 * Mescla execuções de treino e dieta numa única lista ordenada por data desc.
 * Cada item é um card expansível com ícone de status, data/hora, duração
 * (treino) ou status (dieta) e notas. Sem dependência de Next/router — a
 * navegação é client-side via React Router no nível da view.
 */

interface HistoryTimelineProps {
  executions: StudentExecution[];
  dietExecutions?: StudentDietExecution[];
}

type UnifiedEntry =
  | { kind: "workout"; ts: number; exec: StudentExecution }
  | { kind: "diet"; ts: number; exec: StudentDietExecution };

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatDuration(min: number | null | undefined): string {
  if (min == null || !Number.isFinite(min)) return "—";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

type Status = StudentExecution["status"];

function statusIcon(status: Status) {
  if (status === "completed") return CheckCircle2;
  if (status === "abandoned") return XCircle;
  return CircleDashed;
}

function statusColor(status: Status): string {
  if (status === "completed") return "text-accent-soft";
  if (status === "abandoned") return "text-danger";
  return "text-yellow-400/90";
}

function statusLabel(status: Status): string {
  if (status === "completed") return "Completo";
  if (status === "abandoned") return "Abandonado";
  return "Em andamento";
}

function countCompletedWorkoutItems(items: StudentExecutionItem[]): number {
  return items.filter((it) => !!it.completed_at).length;
}

function countCompletedDietItems(items: StudentDietExecutionItem[]): number {
  return items.filter((it) => !!it.completed_at).length;
}

function dietStatusLabel(s: StudentDietExecutionItem["status"]): string | null {
  if (s === "as_prescribed") return "Como prescrito";
  if (s === "modified") return "Modificado";
  if (s === "skipped") return "Pulado";
  return null;
}

// ─── Componente ─────────────────────────────────────────────────────────────

export function HistoryTimeline({
  executions,
  dietExecutions = [],
}: HistoryTimelineProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Mescla e ordena por data desc — imutável (concat + sort em cópia).
  const entries = useMemo<UnifiedEntry[]>(() => {
    const workouts: UnifiedEntry[] = executions.map((exec) => ({
      kind: "workout",
      ts: Date.parse(exec.executed_at),
      exec,
    }));
    const diets: UnifiedEntry[] = dietExecutions.map((exec) => ({
      kind: "diet",
      ts: Date.parse(exec.executed_at),
      exec,
    }));
    return [...workouts, ...diets].sort((a, b) => b.ts - a.ts);
  }, [executions, dietExecutions]);

  return (
    <ol className="space-y-2 animate-fade-in">
      {entries.map((entry) => {
        const id = `${entry.kind}-${entry.exec.id}`;
        const isOpen = expandedId === id;
        const onToggle = () => setExpandedId(isOpen ? null : id);
        return (
          <li key={id}>
            {entry.kind === "workout" ? (
              <WorkoutCard execution={entry.exec} open={isOpen} onToggle={onToggle} />
            ) : (
              <DietCard execution={entry.exec} open={isOpen} onToggle={onToggle} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ─── WorkoutCard ──────────────────────────────────────────────────────────────

interface WorkoutCardProps {
  execution: StudentExecution;
  open: boolean;
  onToggle: () => void;
}

function WorkoutCard({ execution, open, onToggle }: WorkoutCardProps) {
  const completedItems = countCompletedWorkoutItems(execution.items);
  const totalItems = execution.items.length;
  const iconColor = statusColor(execution.status);

  return (
    <article className="rounded-2xl border border-border-subtle bg-surface transition-colors hover:border-border-strong">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className={cn("shrink-0", iconColor)} aria-hidden>
          <Dumbbell size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-ink">
            {execution.workout_title || "Treino"}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted">
            <span>{formatDate(execution.executed_at)}</span>
            <span className="text-faint">·</span>
            <span>{formatTime(execution.executed_at)}</span>
            <span className="text-faint">·</span>
            <span className="inline-flex items-center gap-1">
              <Clock size={10} aria-hidden />
              {formatDuration(execution.duration_minutes)}
            </span>
            {totalItems > 0 ? (
              <>
                <span className="text-faint">·</span>
                <span>
                  {completedItems}/{totalItems} ex.
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="hidden text-right sm:block">
          <span className={cn("text-[11px] uppercase tracking-wider", iconColor)}>
            {statusLabel(execution.status)}
          </span>
        </div>
        <span className="shrink-0 text-faint" aria-hidden>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {open ? (
        <div className="border-t border-border-subtle px-4 py-3">
          {execution.items.length === 0 ? (
            <p className="text-sm text-muted">Sem registros desta execução.</p>
          ) : (
            <ul className="divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-elevated/40">
              {execution.items.map((it) => (
                <li key={it.id} className="px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="truncate text-sm font-medium text-ink">
                      {it.exercise_name || "Exercício"}
                    </p>
                    {it.completed_at ? (
                      <CheckCircle2
                        size={13}
                        className="shrink-0 text-accent-soft"
                        aria-label="Concluído"
                      />
                    ) : (
                      <CircleDashed
                        size={13}
                        className="shrink-0 text-faint"
                        aria-label="Não concluído"
                      />
                    )}
                  </div>
                  <WorkoutItemMetrics item={it} />
                  {it.notes ? (
                    <p className="mt-1 text-xs italic text-muted">&ldquo;{it.notes}&rdquo;</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {execution.notes ? (
            <div className="mt-3 rounded-lg border border-border-subtle bg-elevated/40 px-3 py-2 text-xs text-muted">
              <span className="block uppercase tracking-wider text-faint">
                Notas da sessão
              </span>
              <span className="mt-0.5 block text-ink">{execution.notes}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function WorkoutItemMetrics({ item }: { item: StudentExecutionItem }) {
  const parts: string[] = [];
  if (item.sets_done != null) parts.push(`${item.sets_done} séries`);
  if (item.reps_per_set?.length) parts.push(`${item.reps_per_set.join(" / ")} reps`);
  if (item.load_kg != null) parts.push(`${item.load_kg} kg`);
  if (item.rpe != null) parts.push(`RPE ${item.rpe}`);
  if (parts.length === 0) return null;
  return <p className="mt-0.5 text-xs text-muted">{parts.join(" · ")}</p>;
}

// ─── DietCard ─────────────────────────────────────────────────────────────────

interface DietCardProps {
  execution: StudentDietExecution;
  open: boolean;
  onToggle: () => void;
}

function DietCard({ execution, open, onToggle }: DietCardProps) {
  const completedItems = countCompletedDietItems(execution.items);
  const totalItems = execution.items.length;
  const Icon = statusIcon(execution.status);
  const iconColor = statusColor(execution.status);

  return (
    <article className="rounded-2xl border border-border-subtle bg-surface transition-colors hover:border-border-strong">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className={cn("shrink-0", iconColor)} aria-hidden>
          <Utensils size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-ink">
            {execution.diet_title || "Refeição"}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted">
            <span>{formatDate(execution.executed_at)}</span>
            <span className="text-faint">·</span>
            <span>{formatTime(execution.executed_at)}</span>
            <span className="text-faint">·</span>
            <span className={cn("inline-flex items-center gap-1", iconColor)}>
              <Icon size={11} aria-hidden />
              {statusLabel(execution.status)}
            </span>
            {totalItems > 0 ? (
              <>
                <span className="text-faint">·</span>
                <span>
                  {completedItems}/{totalItems} alimentos
                </span>
              </>
            ) : null}
          </div>
        </div>
        <span className="shrink-0 text-faint" aria-hidden>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {open ? (
        <div className="border-t border-border-subtle px-4 py-3">
          {execution.items.length === 0 ? (
            <p className="text-sm text-muted">Sem registros desta refeição.</p>
          ) : (
            <ul className="divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-elevated/40">
              {execution.items.map((it) => {
                const sl = dietStatusLabel(it.status);
                return (
                  <li key={it.id} className="px-3 py-2.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-sm font-medium text-ink">
                        {it.food_name || "Alimento"}
                      </p>
                      {it.completed_at ? (
                        <CheckCircle2
                          size={13}
                          className="shrink-0 text-accent-soft"
                          aria-label="Marcado"
                        />
                      ) : (
                        <CircleDashed
                          size={13}
                          className="shrink-0 text-faint"
                          aria-label="Não marcado"
                        />
                      )}
                    </div>
                    {sl ? (
                      <p className="mt-0.5 text-[11px] uppercase tracking-wider text-accent-soft">
                        {sl}
                      </p>
                    ) : null}
                    {it.ate_what ? (
                      <p className="mt-0.5 text-xs text-muted">{it.ate_what}</p>
                    ) : null}
                    {it.notes ? (
                      <p className="mt-1 text-xs italic text-muted">&ldquo;{it.notes}&rdquo;</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {execution.notes ? (
            <div className="mt-3 rounded-lg border border-border-subtle bg-elevated/40 px-3 py-2 text-xs text-muted">
              <span className="block uppercase tracking-wider text-faint">
                Notas da sessão
              </span>
              <span className="mt-0.5 block text-ink">{execution.notes}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
